import React, { useContext, useMemo, useState } from "react";
import { useHistory, useParams, Redirect } from "react-router-dom";
import { makeStyles, useTheme, createTheme, ThemeProvider } from "@material-ui/core/styles";
import { Paper, Drawer, IconButton, useMediaQuery } from "@material-ui/core";

import BusinessRoundedIcon from "@material-ui/icons/BusinessRounded";
import WorkspacesRoundedIcon from "@material-ui/icons/AccountTreeRounded";
import SubscriptionsRoundedIcon from "@material-ui/icons/CardMembershipRounded";
import DashboardRoundedIcon from "@material-ui/icons/DashboardRounded";
import HistoryRoundedIcon from "@material-ui/icons/HistoryRounded";
import CompareArrowsRoundedIcon from "@material-ui/icons/CompareArrowsRounded";
import DeviceHubRoundedIcon from "@material-ui/icons/DeviceHubRounded";
import DnsRoundedIcon from "@material-ui/icons/DnsRounded";
// MUI v4 has no Campaign (megaphone) glyph; AnnouncementRounded (speech
// bubble with !) is the closest semantic available, same convention as
// the SmartToy→EmojiObjects alias elsewhere in this codebase.
import CampaignRoundedIcon from "@material-ui/icons/AnnouncementRounded";
import ChatRoundedIcon from "@material-ui/icons/ChatRounded";
import OpenInNewRoundedIcon from "@material-ui/icons/OpenInNewRounded";
import DescriptionRoundedIcon from "@material-ui/icons/DescriptionRounded";
import MenuRoundedIcon from "@material-ui/icons/MenuRounded";
import MemoryRoundedIcon from "@material-ui/icons/MemoryRounded";
import ArrowForwardRoundedIcon from "@material-ui/icons/ArrowForwardRounded";

import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import AdminOverview from "../../components/AdminOverview";
import CompaniesManager from "../../components/CompaniesManager";
import PlansManager from "../../components/PlansManager";
import SubscriptionsManager from "../../components/SubscriptionsManager";
import TenantHealthPanel from "../../components/TenantHealthPanel";
import AdminAuditLog from "../../components/AdminAuditLog";
import TenantCompare from "../../components/TenantCompare";
import BroadcastManager from "../../components/BroadcastManager";
import SitePagesManager from "../../components/SitePagesManager";
import AdminConnectionsMonitor from "../../components/AdminConnectionsMonitor";
import CustomDomainsManager from "../../components/CustomDomainsManager";
import CoreEnginePanel from "../../components/CoreEnginePanel";
// SiteContent is a self-contained sub-area (Pages | Navigation | SEO) that
// edits whapiz.com marketing content from inside the Whapiz admin shell.
// Routed via the existing /admin/:tab? mount under the tab id "site-content".
import SiteContent from "./SiteContent";

// ───── Mobile design tokens (per /tmp/mobile-design-system.md A.2-A.6) ─────
// Single source of truth for spacing, touch targets, typography ramp,
// border radius, and the brand palette. DO NOT introduce values outside
// these tables — see Section E (anti-patterns) of the spec.
const SP = {
  xxs: 4, xs: 8, sm: 12, md: 16, lg: 20, xl: 24, xxl: 32, hero: 48,
};
const TT = { min: 44, primary: 48, hero: 56 };
const TYPE = {
  caption:    { fontSize: 12, lineHeight: 1.4, letterSpacing: 0.2 },
  body:       { fontSize: 14, lineHeight: 1.5 },
  bodyStrong: { fontSize: 15, lineHeight: 1.45, fontWeight: 500 },
  subheading: { fontSize: 17, lineHeight: 1.4,  fontWeight: 500 },
  heading:    { fontSize: 20, lineHeight: 1.3,  fontWeight: 600 },
  display:    { fontSize: 24, lineHeight: 1.25, fontWeight: 700 },
};
const RAD = { pill: 999, sm: 8, md: 12, lg: 16, xl: 20 };
const COL = {
  brandGreen:     "#25d366",
  brandGreenDeep: "#128c7e",
  brandGreenSoft: "rgba(37,211,102,0.08)",
  textPrimary:    "#0B0D0C",
  textSecondary:  "#52525b",
  borderSoft:     "#eaeaea",
  pageBg:         "#f7faf8",
  cardBg:         "#ffffff",
  destructive:    "#ef4444",
  warning:        "#f59e0b",
};

// Whapiz admin shell. Distinct from the tenant CRM layout — super
// admins land here on login (handleLogin redirects when user.super)
// and the GoDeal sidebar is intentionally NOT mounted around this
// page. Visual grammar: light surface, Roboto-only typography
// (per Raviv 2026-05-19), single accent green.
const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  // CRM canonical accent vars — identical to Tags/Campaigns/Contacts
  // so the admin shell reads as part of the same design system.
  const accent     = isDark ? "#2ee37a" : "#128c7e";
  const accentSoft = isDark ? "rgba(46,227,122,0.08)" : "rgba(18,140,126,0.06)";
  const hair       = isDark ? "rgba(255,255,255,0.08)" : "rgba(15,22,20,0.06)";
  const fg         = isDark ? "rgba(255,255,255,0.92)" : "#0f1614";
  const fgMuted    = isDark ? "rgba(255,255,255,0.55)" : "rgba(15,22,20,0.55)";
  const surface    = isDark ? "#13181a" : "#ffffff";
  const pageBg     = isDark ? "#0c1012" : COL.pageBg;
  return {
    page: {
      // minHeight (not height) so iOS Safari can shrink its toolbars and
      // the page can still grow with content — see Section E anti-patterns
      // (height:100vh on page roots breaks iOS toolbar shrinking).
      minHeight: "100vh",
      background: pageBg,
      direction: theme.direction,
      display: "flex",
      flexDirection: "column",
      // Roboto-only per Raviv 2026-05-19 — no Manrope/Heebo/Fraunces here.
      fontFamily: "'Roboto',system-ui,-apple-system,'Segoe UI','Helvetica Neue',sans-serif",
    },
    // 4px WhatsApp green band at the very top — visual signature that
    // brands the admin shell without intruding on the topbar chrome.
    brandStripe: {
      height: 4,
      background: `linear-gradient(90deg, ${COL.brandGreen} 0%, ${COL.brandGreenDeep} 100%)`,
      flexShrink: 0,
    },
    topbar: {
      // Sticky NOT fixed on mobile per Section E anti-patterns — keeps iOS
      // toolbar shrinking intact. Desktop keeps the same 64 px chrome.
      height: 64,
      // CRM-canonical: subtle accentSoft wash + hairline bottom border
      // instead of the previous solid white. Reads as part of the same
      // design system as Tags / Campaigns / Contacts.
      background: accentSoft,
      borderBottom: `1px solid ${hair}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: `0 ${SP.xl}px`,
      flexShrink: 0,
      // Mobile: tighter horizontal padding (SP.sm = 12) per A.2.
      [theme.breakpoints.down("xs")]: {
        height: 56,
        padding: `0 ${SP.sm}px`,
      },
    },
    brandWrap: {
      display: "inline-flex",
      alignItems: "center",
      gap: 12,
    },
    brandChip: {
      width: 36,
      height: 36,
      borderRadius: 10,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      background: `linear-gradient(135deg, ${COL.brandGreen} 0%, ${COL.brandGreenDeep} 100%)`,
      color: "#fff",
      boxShadow: "0 6px 14px rgba(18, 140, 126, 0.25)",
      "& svg": { fontSize: 18 },
      flexShrink: 0,
    },
    // Brand label block: stacks the 0.22em "SUPER ADMIN" eyebrow above
    // the Whapiz wordmark so the eyebrow reads as a deliberate role tag
    // (same uppercase/0.22em rhythm as CRM table heads).
    brandLabelBlock: {
      display: "inline-flex",
      flexDirection: "column",
      justifyContent: "center",
      gap: 2,
      lineHeight: 1,
    },
    brandEyebrow: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color: accent,
      lineHeight: 1,
    },
    brandText: {
      // Roboto bold 17 #0f1614 per brief.
      fontFamily: "'Roboto',system-ui,-apple-system,sans-serif",
      fontWeight: 700,
      fontSize: 17,
      letterSpacing: -0.2,
      color: fg,
      lineHeight: 1.1,
    },
    exitLink: {
      // Outlined WhatsApp green button per brief — 40 px height, green
      // border + green text, accentSoft on hover.
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      height: 40,
      fontSize: 13,
      fontWeight: 600,
      color: accent,
      background: "transparent",
      border: `1px solid ${accent}`,
      borderRadius: RAD.pill,
      padding: "0 16px",
      cursor: "pointer",
      transition: "all 160ms ease",
      fontFamily: "inherit",
      letterSpacing: 0.1,
      "& svg": { fontSize: 15 },
      "&:hover": {
        background: accentSoft,
        color: accent,
        borderColor: accent,
      },
      // Mobile: collapse to icon-only square so the topbar doesn't
      // overflow next to the brand on narrow viewports. Tap zone bumped
      // to TT.min (44 px) per A.3.
      [theme.breakpoints.down("xs")]: {
        minHeight: TT.min,
        minWidth: TT.min,
        height: TT.min,
        padding: 0,
        justifyContent: "center",
        "& .exitLinkText": {
          position: "absolute",
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: "hidden",
          clip: "rect(0,0,0,0)",
          whiteSpace: "nowrap",
          border: 0,
        },
      },
    },
    body: {
      flex: 1,
      display: "grid",
      // minmax(0,1fr) clamps the content track so wide children (e.g.
      // SubscriptionsManager's 720-px table) cannot expand the 1fr
      // column past the viewport. Without the 0 floor the track grows
      // to min-content and pushes the panelCard, breaking header
      // alignment.
      gridTemplateColumns: "240px minmax(0, 1fr)",
      // Phone: drop the permanent nav column entirely — it's rendered
      // as a temporary Drawer driven by the hamburger in the topbar.
      // Without this override the 240 px column would stack a ~400 px
      // vertical list above the content panel.
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "minmax(0, 1fr)" },
    },
    nav: {
      background: surface,
      borderInlineEnd: `1px solid ${hair}`,
      padding: "22px 14px",
      display: "flex",
      flexDirection: "column",
      gap: 2,
      // Hidden on phones; the same item list renders inside the Drawer
      // via classes.mobileNav.
      [theme.breakpoints.down("xs")]: { display: "none" },
    },
    mobileNav: {
      width: 280,
      padding: "18px 12px",
      display: "flex",
      flexDirection: "column",
      gap: 0,
      background: surface,
      height: "100%",
      boxSizing: "border-box",
    },
    // Drawer paper override applied via PaperProps — gives the temporary
    // drawer the same surface tone as the rest of the shell instead of
    // MUI's default flat white in dark mode.
    drawerPaper: {
      background: surface,
      borderInlineEnd: `1px solid ${hair}`,
    },
    hamburger: {
      // Only renders on xs (display:none on larger breakpoints) — opens
      // the temporary nav drawer. Mobile tap target ≥44 px per A.3.
      display: "none",
      marginInlineEnd: SP.xs,
      padding: 6,
      color: fg,
      [theme.breakpoints.down("xs")]: {
        display: "inline-flex",
        minHeight: TT.min,
        minWidth: TT.min,
        padding: 10,
      },
    },
    navHeading: {
      // CRM canonical eyebrow — same 10.5/0.22em/uppercase/accent rhythm
      // as Tags / Campaigns / Contacts table heads.
      fontSize: 10.5,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.22em",
      color: accent,
      padding: "0 12px 12px",
    },
    navItem: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "10px 12px",
      borderRadius: 10,
      cursor: "pointer",
      color: fg,
      fontSize: 14,
      fontWeight: 500,
      transition: "background 140ms ease, color 140ms ease",
      position: "relative",
      "&:hover": { background: accentSoft, color: accent },
      "& svg": { fontSize: 18, color: fgMuted, transition: "color 140ms ease" },
      "&:hover svg": { color: accent },
      // Mobile: row height ≥44 px (TT.min) — touch target rule A.3.
      [theme.breakpoints.down("xs")]: {
        minHeight: TT.min,
        padding: "12px 12px",
      },
    },
    navItemActive: {
      background: accentSoft,
      color: accent,
      fontWeight: 600,
      "&:hover": { background: accentSoft, color: accent },
      "& svg": { color: accent },
    },
    // Mobile drawer item gets hairline dividers between items per brief —
    // composed alongside navItem so behaviour stays identical.
    mobileNavItem: {
      borderBottom: `1px solid ${hair}`,
      borderRadius: 0,
      "&:last-child": { borderBottom: "none" },
    },
    content: {
      // Page horizontal padding per A.2: desktop SP.xl (24), mobile SP.sm (12).
      padding: SP.xl,
      // minWidth:0 is required for grid children that hold wide
      // content (e.g. tables with explicit min-widths). Without it the
      // child can blow past the 1fr track even with minmax(0,1fr) on
      // the parent because flex/grid children default to min-content.
      minWidth: 0,
      [theme.breakpoints.down("xs")]: { padding: SP.sm },
    },
    // Launcher grid — renders on the overview tab as the primary nav
    // surface. 4-up on desktop, collapses to 2-up on xs per brief.
    navGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
      gap: SP.md,
      marginBottom: SP.xl,
      [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      },
      [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      },
      [theme.breakpoints.down("xs")]: {
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: SP.sm,
        marginBottom: SP.md,
      },
    },
    // Section eyebrow above the nav grid — uses canonical CRM rhythm.
    navGridHeading: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color: accent,
      marginBottom: SP.sm,
      paddingInline: 2,
    },
    navCard: {
      position: "relative",
      display: "flex",
      flexDirection: "column",
      gap: SP.sm,
      padding: SP.md,
      background: surface,
      border: `1px solid ${hair}`,
      borderRadius: 16,
      cursor: "pointer",
      transition: "transform 0.18s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.18s ease, background 0.14s ease, border-color 0.14s ease",
      boxShadow: isDark
        ? "0 1px 0 rgba(255,255,255,0.04) inset, 0 8px 24px rgba(0,0,0,0.28)"
        : "0 1px 0 rgba(15,22,20,0.02), 0 8px 24px rgba(15,22,20,0.04)",
      minHeight: 132,
      "&:hover": {
        background: accentSoft,
        borderColor: isDark ? "rgba(46,227,122,0.24)" : "rgba(18,140,126,0.2)",
        transform: "translateY(-2px)",
        boxShadow: isDark
          ? "0 1px 0 rgba(255,255,255,0.04) inset, 0 14px 28px rgba(0,0,0,0.36)"
          : "0 12px 28px rgba(15,22,20,0.08), 0 2px 6px rgba(15,22,20,0.04)",
      },
      "&:focus-visible": {
        outline: "none",
        borderColor: accent,
        boxShadow: `0 0 0 3px ${accentSoft}`,
      },
      "&:active": {
        transform: "translateY(-1px) scale(0.99)",
      },
      [theme.breakpoints.down("xs")]: {
        padding: SP.sm,
        gap: SP.xs,
        minHeight: 116,
        borderRadius: 14,
      },
    },
    navCardIcon: {
      width: 40,
      height: 40,
      borderRadius: 10,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      background: accentSoft,
      color: accent,
      "& svg": { fontSize: 22, color: accent },
      // Brief calls for a 32px icon — the chip is 40 to give it ink room,
      // but the glyph itself sits at 22 here so two-line labels stay
      // balanced. On larger breakpoints we promote to 32.
      [theme.breakpoints.up("sm")]: {
        width: 44,
        height: 44,
        "& svg": { fontSize: 26 },
      },
    },
    navCardLabel: {
      // Roboto medium 14 per brief.
      fontFamily: "'Roboto',system-ui,-apple-system,sans-serif",
      fontSize: 14,
      fontWeight: 600,
      lineHeight: 1.3,
      color: fg,
      letterSpacing: -0.1,
    },
    navCardDesc: {
      // Roboto 12 fgMuted per brief, single line clamp so cards stay
      // height-aligned in the grid.
      fontFamily: "'Roboto',system-ui,-apple-system,sans-serif",
      fontSize: 12,
      fontWeight: 400,
      lineHeight: 1.4,
      color: fgMuted,
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    },
    navCardActive: {
      borderColor: accent,
      background: accentSoft,
      "&:hover": {
        background: accentSoft,
        borderColor: accent,
      },
    },
    // Small arrow chip in the corner — fades in on hover so the card
    // doesn't feel cluttered at rest.
    navCardArrow: {
      position: "absolute",
      top: SP.md,
      insetInlineEnd: SP.md,
      width: 24,
      height: 24,
      borderRadius: 999,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      color: fgMuted,
      opacity: 0,
      transform: "translateX(-4px)",
      transition: "opacity 0.18s ease, transform 0.18s ease, color 0.18s ease",
      "& svg": { fontSize: 16 },
      "$navCard:hover &": {
        opacity: 1,
        transform: "translateX(0)",
        color: accent,
      },
      [theme.breakpoints.down("xs")]: {
        top: SP.sm,
        insetInlineEnd: SP.sm,
      },
    },
    panelCard: {
      background: surface,
      borderRadius: 18,
      border: `1px solid ${hair}`,
      // Desktop card padding SP.xl (24), mobile SP.md (16) per A.2.
      padding: SP.xl,
      boxShadow: isDark
        ? "0 1px 0 rgba(255,255,255,0.04) inset, 0 14px 36px rgba(0,0,0,0.32)"
        : "0 14px 36px rgba(15,22,20,0.06), 0 2px 6px rgba(15,22,20,0.04)",
      // Containment guard: wide children (SubscriptionsManager has a
      // 720-px table) must not expand the card past the grid cell. The
      // child's TableContainer already does overflowX:auto; this pair
      // ensures the toolbar/header above it stays card-aligned.
      width: "100%",
      maxWidth: "100%",
      boxSizing: "border-box",
      minWidth: 0,
      overflow: "hidden",
      // Phone: tighter inner padding so panel children (which often host
      // their own wide tables / forms) get more usable width. Each
      // child still ships its own overflow guarding —
      // CompaniesManager:839 is the canonical example. Mobile radius
      // RAD.md (12) per A.5 / B.6.
      [theme.breakpoints.down("xs")]: {
        padding: SP.md,
        borderRadius: 14,
      },
    },
  };
});

const AdminShell = () => {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  // xsScreen drives the temporary Drawer's open/close behaviour. On
  // phones the nav is hidden behind a hamburger toggle so the content
  // panel isn't shoved down by ~400 px of stacked nav items.
  const isXs = useMediaQuery(theme.breakpoints.down("xs"));
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { user } = useContext(AuthContext);
  const params = useParams();
  // Two route shapes feed this page:
  //   /admin/:tab?            → Overview / Companies / Plans / Subscriptions
  //   /admin/companies/:id    → per-tenant TenantHealthPanel drill-down
  // When params.id is present we're on the drill-down, so the sidebar
  // nav still highlights "Companies" since that's the source tab.
  const tenantId = params.id || null;
  const tab = tenantId ? "companies" : params.tab || "overview";

  const items = useMemo(() => ([
    {
      id: "overview",
      label: i18n.t("admin.nav.overview", { defaultValue: "Overview" }),
      desc: i18n.t("admin.nav.overviewDesc", { defaultValue: "Platform snapshot and key signals" }),
      Icon: DashboardRoundedIcon,
    },
    {
      id: "companies",
      label: i18n.t("admin.nav.companies", { defaultValue: "Companies" }),
      desc: i18n.t("admin.nav.companiesDesc", { defaultValue: "Tenant directory and health" }),
      Icon: BusinessRoundedIcon,
    },
    {
      id: "plans",
      label: i18n.t("admin.nav.plans", { defaultValue: "Plans" }),
      desc: i18n.t("admin.nav.plansDesc", { defaultValue: "Pricing tiers and entitlements" }),
      Icon: WorkspacesRoundedIcon,
    },
    {
      id: "subscriptions",
      label: i18n.t("admin.nav.subscriptions", { defaultValue: "Subscriptions" }),
      desc: i18n.t("admin.nav.subscriptionsDesc", { defaultValue: "Active billing and renewals" }),
      Icon: SubscriptionsRoundedIcon,
    },
    {
      id: "compare",
      label: i18n.t("admin.nav.compare", { defaultValue: "Compare" }),
      desc: i18n.t("admin.nav.compareDesc", { defaultValue: "Side-by-side tenant metrics" }),
      Icon: CompareArrowsRoundedIcon,
    },
    {
      id: "broadcasts",
      label: i18n.t("admin.nav.broadcasts", { defaultValue: "Broadcasts" }),
      desc: i18n.t("admin.nav.broadcastsDesc", { defaultValue: "System-wide announcements" }),
      Icon: CampaignRoundedIcon,
    },
    {
      id: "audit",
      label: i18n.t("admin.nav.audit", { defaultValue: "Audit log" }),
      desc: i18n.t("admin.nav.auditDesc", { defaultValue: "Admin actions and change history" }),
      Icon: HistoryRoundedIcon,
    },
    {
      id: "pages",
      label: i18n.t("admin.nav.pages", { defaultValue: "Pages" }),
      desc: i18n.t("admin.nav.pagesDesc", { defaultValue: "Legacy in-app page payloads" }),
      Icon: DescriptionRoundedIcon,
    },
    {
      // New super-admin sub-area: marketing content (Pages | Navigation | SEO)
      // for whapiz.com. Separate from the legacy "pages" tab which edits the
      // older /api/admin/pages payload via SitePagesManager.
      id: "site-content",
      label: i18n.t("admin.nav.siteContent", { defaultValue: "Site Content" }),
      desc: i18n.t("admin.nav.siteContentDesc", { defaultValue: "whapiz.com pages, navigation, SEO" }),
      Icon: DescriptionRoundedIcon,
    },
    {
      id: "connections",
      label: i18n.t("admin.nav.connections", { defaultValue: "Connections" }),
      desc: i18n.t("admin.nav.connectionsDesc", { defaultValue: "WhatsApp session monitor" }),
      Icon: DeviceHubRoundedIcon,
    },
    {
      id: "domains",
      label: i18n.t("admin.nav.domains", { defaultValue: "Custom Domains" }),
      desc: i18n.t("admin.nav.domainsDesc", { defaultValue: "Tenant custom domains and where they point" }),
      Icon: DnsRoundedIcon,
    },
    {
      id: "core",
      label: i18n.t("admin.nav.core", { defaultValue: "Core engine" }),
      desc: i18n.t("admin.nav.coreDesc", { defaultValue: "What the Brain knows about our own codebase" }),
      Icon: MemoryRoundedIcon,
    },
  ]), []);

  // Belt-and-suspenders guard. Routes/Route already gates isPrivate,
  // but if a tenant admin somehow lands here we bounce them to their
  // workspace. Backend isSuper middleware is the real authority.
  // Placed AFTER hook calls so the hooks-order rule isn't violated
  // on the early-return path.
  if (user && user.id && !user.super) {
    return <Redirect to="/" />;
  }

  const renderPanel = () => {
    // Drill-down route takes priority over tab routing.
    if (tenantId) return <TenantHealthPanel />;
    switch (tab) {
      case "companies":     return <CompaniesManager />;
      case "plans":         return <PlansManager />;
      case "subscriptions": return <SubscriptionsManager />;
      case "audit":         return <AdminAuditLog />;
      case "compare":       return <TenantCompare />;
      case "broadcasts":    return <BroadcastManager />;
      case "pages":         return <SitePagesManager />;
      case "site-content":  return <SiteContent />;
      case "connections":   return <AdminConnectionsMonitor />;
      case "domains":       return <CustomDomainsManager />;
      case "core":          return <CoreEnginePanel />;
      case "overview":
      default:              return (
        <AdminOverview
          onOpenCompanies={() => history.push("/admin/companies")}
          onOpenCompaniesFiltered={(filter) =>
            history.push(`/admin/companies?status=${encodeURIComponent(filter)}`)
          }
          onOpenAuditLog={() => history.push("/admin/audit")}
        />
      );
    }
  };

  // Same item list, two rendering targets: the permanent .nav column on
  // sm+ and the temporary Drawer on xs. Extracted so both stay in sync
  // when the items[] array changes.
  const renderNavItems = (onItemClick, { mobile = false } = {}) =>
    items.map(({ id, label, Icon }) => (
      <div
        key={id}
        role="button"
        tabIndex={0}
        className={`${classes.navItem} ${tab === id ? classes.navItemActive : ""} ${mobile ? classes.mobileNavItem : ""}`}
        onClick={() => {
          history.push(`/admin/${id}`);
          if (onItemClick) onItemClick();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            history.push(`/admin/${id}`);
            if (onItemClick) onItemClick();
          }
        }}
      >
        <Icon />
        <span>{label}</span>
      </div>
    ));

  // Launcher grid — renders on the overview tab in place of the legacy
  // AdminOverview wall of charts. Provides a single tap-target per
  // admin sub-area with icon + label + 1-line description per brief.
  const renderNavGrid = () => (
    <>
      <div className={classes.navGridHeading}>
        {i18n.t("admin.nav.heading", { defaultValue: "Platform" })}
      </div>
      <div className={classes.navGrid}>
        {items
          .filter((it) => it.id !== "overview")
          .map(({ id, label, desc, Icon }) => (
            <div
              key={id}
              role="button"
              tabIndex={0}
              className={`${classes.navCard} ${tab === id ? classes.navCardActive : ""}`}
              onClick={() => history.push(`/admin/${id}`)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  history.push(`/admin/${id}`);
                }
              }}
            >
              <span className={classes.navCardIcon}>
                <Icon />
              </span>
              <div className={classes.navCardLabel}>{label}</div>
              <div className={classes.navCardDesc}>{desc}</div>
              <span className={classes.navCardArrow}>
                <ArrowForwardRoundedIcon />
              </span>
            </div>
          ))}
      </div>
    </>
  );

  return (
    <div className={classes.page}>
      <div className={classes.brandStripe} aria-hidden="true" />
      <header className={classes.topbar}>
        <div className={classes.brandWrap}>
          {/* Hamburger appears only on xs (CSS-gated). Drives the same
              navigation as the permanent sidebar. */}
          <IconButton
            className={classes.hamburger}
            onClick={() => setMobileNavOpen(true)}
            aria-label={i18n.t("admin.nav.openMenu", { defaultValue: "Open menu" })}
            size="small"
          >
            <MenuRoundedIcon />
          </IconButton>
          <span className={classes.brandChip}><ChatRoundedIcon /></span>
          <span className={classes.brandLabelBlock}>
            <span className={classes.brandEyebrow}>
              {i18n.t("admin.eyebrow", { defaultValue: "Super Admin" })}
            </span>
            <span className={classes.brandText}>Whapiz</span>
          </span>
        </div>
        <button
          type="button"
          className={classes.exitLink}
          onClick={() => history.push("/tickets")}
          title={i18n.t("admin.enterSoftware", { defaultValue: "Enter software" })}
          aria-label={i18n.t("admin.enterSoftware", { defaultValue: "Enter software" })}
        >
          <span className="exitLinkText">
            {i18n.t("admin.enterSoftware", { defaultValue: "Enter software" })}
          </span>
          <OpenInNewRoundedIcon />
        </button>
      </header>

      <div className={classes.body}>
        <nav className={classes.nav}>
          <div className={classes.navHeading}>
            {i18n.t("admin.nav.heading", { defaultValue: "Platform" })}
          </div>
          {renderNavItems()}
        </nav>

        {/* Mobile-only temporary drawer. Auto-closes after a nav item
            is tapped (passed via renderNavItems callback) and on
            backdrop click via MUI's built-in temporary variant. */}
        <Drawer
          anchor={theme.direction === "rtl" ? "right" : "left"}
          open={isXs && mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
          variant="temporary"
          PaperProps={{ className: classes.drawerPaper }}
        >
          <div className={classes.mobileNav}>
            <div className={classes.navHeading}>
              {i18n.t("admin.nav.heading", { defaultValue: "Platform" })}
            </div>
            {renderNavItems(() => setMobileNavOpen(false), { mobile: true })}
          </div>
        </Drawer>

        <div className={classes.content}>
          {tab === "overview" && !tenantId && renderNavGrid()}
          <Paper className={classes.panelCard} elevation={0}>
            {renderPanel()}
          </Paper>
        </div>
      </div>
    </div>
  );
};

const Admin = () => {
  // Whapiz Platform theme — forced dark, independent of the tenant's light/dark
  // toggle. AdminShell renders UNDER this provider so its own makeStyles (and
  // every child manager's) resolve theme.palette.type === "dark".
  const platformTheme = useMemo(
    () =>
      createTheme({
        palette: {
          type: "dark",
          primary: { main: "#2ee37a" },
          background: { default: "#0C1012", paper: "#13181A" },
          text: { primary: "#f4f5f6", secondary: "#a8acb3" },
        },
        overrides: {
          MuiPaper: { root: { backgroundColor: "#13181A" } },
          MuiCard: {
            root: {
              backgroundColor: "#1A1E1B",
              border: "1px solid rgba(255,255,255,0.06)",
            },
          },
          MuiTableHead: { root: { backgroundColor: "rgba(255,255,255,0.03)" } },
          MuiOutlinedInput: { root: { backgroundColor: "#1F2421" } },
          MuiTabs: { indicator: { backgroundColor: "#2ee37a" } },
        },
      }),
    []
  );

  return (
    <ThemeProvider theme={platformTheme}>
      <AdminShell />
    </ThemeProvider>
  );
};

export default Admin;
